<script setup lang="ts">
import type { Hours } from "~/models/Hours"
import type { Shop } from "~/models/Shop"
import { useForm } from "~/composables/form"

/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const shopListStore = useShopListStore()
const i18n = useI18n()
/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */
function openNetwork(url: string) {
    window.open(url)
}

function scheduleHours(hours: Hours[]): string {
    return hours.map((i) => i.timeInit + " - " + i.timeEnd).join("/")
}

function scheduleDays(days: number[]): string {
    const daysNames = [
        i18n.t("week_days.monday"),
        i18n.t("week_days.tuesday"),
        i18n.t("week_days.wednesday"),
        i18n.t("week_days.thursday"),
        i18n.t("week_days.friday"),
        i18n.t("week_days.saturday"),
        i18n.t("week_days.sunday"),
    ]

    if (days.length === 0) return ""

    days.sort((a, b) => a - b)

    let rangeString = ""
    let start = days[0]
    let end = days[0]

    for (let i = 1; i < days.length; i++) {
        if (days[i] === end + 1) {
            end = days[i]
        } else {
            if (start === end) {
                rangeString += daysNames[start - 1] + ", "
            } else {
                rangeString +=
                    daysNames[start - 1] + " - " + daysNames[end - 1] + ", "
            }
            start = days[i]
            end = days[i]
        }
    }

    if (start === end) {
        rangeString += daysNames[start - 1]
    } else {
        rangeString += daysNames[start - 1] + " - " + daysNames[end - 1]
    }

    return rangeString
}

function storeBrands(shop: Shop): { url: string; icon: string }[] {
    let networks = []
    if (shop.contacts.facebook) {
        networks.push({
            url: shop.contacts.facebook,
            icon: "fa-brands fa-facebook",
        })
    }
    if (shop.contacts.whatsapp) {
        networks.push({
            url: shop.contacts.whatsapp,
            icon: "fa-brands fa-whatsapp",
        })
    }
    if (shop.contacts.instagram) {
        networks.push({
            url: shop.contacts.instagram,
            icon: "fa-brands fa-instagram",
        })
    }

    return networks
}

/**
 *
 *  Props and emits section
 *
 *
 */

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const form = useForm({
    email: "",
    privacy_policy_accepted: false,
})

const notify = useNotification()

const config = useRuntimeConfig()
const turnstileEnabled = computed(() =>
    Boolean(config.public.TURNSTILE_ENABLED)
)
const turnstileSiteKey = computed(
    () => (config.public.TURNSTILE_SITE_KEY as string) ?? ""
)

// One shared token for both layout variants (desktop/mobile). Only the visible
// widget is interacted with; either widget produces a valid token for the page.
const turnstileToken = ref("")
const turnstileDesktopRef = ref<{ reset: () => void } | null>(null)
const turnstileMobileRef = ref<{ reset: () => void } | null>(null)

function resetTurnstile() {
    turnstileToken.value = ""
    turnstileDesktopRef.value?.reset()
    turnstileMobileRef.value?.reset()
}

// Submit is blocked until the privacy policy is accepted and (when enabled) the
// Turnstile challenge is solved.
const canSubmit = computed(
    () =>
        Boolean(form.privacy_policy_accepted) &&
        (!turnstileEnabled.value || Boolean(turnstileToken.value))
)

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */

function submit() {
    if (!form.privacy_policy_accepted) {
        notify("negative", i18n.t("messages.privacy_required"))
        return
    }

    if (turnstileEnabled.value && !turnstileToken.value) {
        notify("negative", i18n.t("messages.captcha_required"))
        return
    }

    const options = turnstileEnabled.value
        ? { additionalData: { "cf-turnstile-response": turnstileToken.value } }
        : undefined

    form.post("subscribers", options)
        .then((response) => {
            form.reset()
            resetTurnstile()
            notify("positive", response.message)
        })
        .catch(() => {
            // Token is single-use; force a fresh challenge for the next attempt.
            resetTurnstile()
        })
}
</script>

<template>
    <div class="tw-bg-customGray-200">
        <!-- Desktop Version: grid 3 columnas para centrar el input -->
        <div
            class="tw-hidden lg:tw-grid lg:tw-grid-cols-[1fr_auto_1fr] tw-w-full tw-h-auto max-container tw-items-center tw-gap-4 tw-py-4"
        >
            <div class="tw-flex tw-flex-row tw-gap-3 tw-items-center">
                <svg
                    class="tw-flex-shrink-0"
                    width="48"
                    height="42"
                    viewBox="0 0 61 52"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M29 42.833C29 31.868 37.8684 22.9997 48.8334 22.9997C51.8934 22.9997 54.755 23.708 57.3334 24.9263V5.99967C57.3334 2.88301 54.7834 0.333008 51.6667 0.333008H6.33335C3.21669 0.333008 0.666687 2.88301 0.666687 5.99967V39.9997C0.666687 43.1163 3.21669 45.6663 6.33335 45.6663H29.2267C29.085 44.7313 29 43.7963 29 42.833ZM6.33335 5.99967L29 20.1663L51.6667 5.99967V11.6663L29 25.833L6.33335 11.6663V5.99967ZM44.13 51.333L34.1 41.303L38.095 37.308L44.1017 43.3147L56.115 31.3013L60.1667 35.2963L44.13 51.333Z"
                        fill="#084298"
                    />
                </svg>
                <div
                    class="tw-flex tw-flex-col tw-items-start tw-justify-center"
                >
                    <p
                        class="tw-leading-tight tw-text-primary tw-font-semibold tw-text-lg tw-whitespace-nowrap"
                    >
                        {{ $t("pre_footer.contact.title") }}
                    </p>
                    <p class="tw-text-primary tw-leading-tight tw-text-sm">
                        {{ $t("pre_footer.contact.subtitle") }}
                    </p>
                </div>
            </div>
            <div
                class="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-justify-center"
            >
                <div class="tw-flex tw-flex-col tw-justify-start">
                    <q-form
                        class="tw-bg-white tw-py-1 tw-px-1 tw-text-customGray-500 tw-rounded-full tw-w-[400px] tw-h-[38px] tw-flex tw-justify-between"
                    >
                        <q-input
                            class="tw-ml-4 tw-self-center tw-w-full"
                            :label="$t('fields.email')"
                            v-model="form.email"
                            :error="form.errors.email"
                            type="email"
                            @update:model-value="form.errors.email = undefined"
                            required
                            borderless
                            autocomplete="off"
                            dense
                        />
                        <button
                            type="button"
                            @click="submit"
                            :disabled="!canSubmit"
                            :class="{
                                'tw-opacity-50 tw-cursor-not-allowed':
                                    !canSubmit,
                            }"
                            class="tw-bg-primary tw-text-white tw-rounded-full tw-font-semibold tw-text-xs tw-leading-4 tw-uppercase tw-px-4 tw-whitespace-nowrap"
                        >
                            {{ $t("pre_footer.contact.button") }}
                        </button>
                    </q-form>
                    <div class="tw-flex tw-items-center tw-mt-2 tw-ml-2">
                        <q-checkbox
                            v-model="form.privacy_policy_accepted"
                            color="primary"
                            size="xs"
                            dense
                        />
                        <span class="tw-text-xs tw-ml-1 tw-text-primary">
                            {{ $t("pre_footer.contact.accept_privacy") }}
                            <NuxtLink
                                to="/information/seg-priv-policy"
                                class="tw-underline tw-font-semibold"
                                >{{
                                    $t("settings.seg_and_privacy_policy")
                                }}</NuxtLink
                            >
                        </span>
                    </div>
                    <span
                        class="tw-text-negative tw-ml-4 tw-text-xs"
                        v-if="form.errors.email"
                        >{{ form.errors.email[0] }}</span
                    >
                </div>
                <TurnstileWidget
                    v-if="turnstileEnabled && turnstileSiteKey"
                    ref="turnstileDesktopRef"
                    v-model="turnstileToken"
                    :site-key="turnstileSiteKey"
                    theme="light"
                    size="flexible"
                />
            </div>
            <div class="tw-flex tw-justify-end tw-items-center">
                <Brands color="primary" />
            </div>
        </div>

        <!-- Mobile & Tablet Version -->
        <div
            class="lg:tw-hidden tw-flex tw-flex-col tw-items-center tw-py-8 tw-px-4 tw-gap-6 tw-max-w-4xl tw-mx-auto"
        >
            <div class="tw-flex tw-flex-row tw-gap-3 tw-items-center">
                <!-- Icon -->
                <svg
                    class="tw-flex-shrink-0"
                    width="58"
                    height="58"
                    viewBox="0 0 61 52"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M29 42.833C29 31.868 37.8684 22.9997 48.8334 22.9997C51.8934 22.9997 54.755 23.708 57.3334 24.9263V5.99967C57.3334 2.88301 54.7834 0.333008 51.6667 0.333008H6.33335C3.21669 0.333008 0.666687 2.88301 0.666687 5.99967V39.9997C0.666687 43.1163 3.21669 45.6663 6.33335 45.6663H29.2267C29.085 44.7313 29 43.7963 29 42.833ZM6.33335 5.99967L29 20.1663L51.6667 5.99967V11.6663L29 25.833L6.33335 11.6663V5.99967ZM44.13 51.333L34.1 41.303L38.095 37.308L44.1017 43.3147L56.115 31.3013L60.1667 35.2963L44.13 51.333Z"
                        fill="#084298"
                    />
                </svg>

                <!-- Titles -->
                <div class="tw-flex tw-flex-col tw-items-start tw-gap-2">
                    <p
                        class="tw-text-primary tw-font-bold tw-text-base md:tw-text-3xl tw-leading-tight"
                    >
                        {{ $t("pre_footer.contact.title") }}
                    </p>
                    <p
                        class="tw-text-primary tw-text-xs md:tw-text-lg tw-leading-tight"
                    >
                        {{ $t("pre_footer.contact.subtitle") }}
                    </p>
                </div>
            </div>

            <!-- Form -->
            <div class="tw-w-full tw-max-w-3xl tw-flex tw-flex-col">
                <q-form
                    class="tw-bg-white tw-py-2 tw-px-2 tw-text-customGray-500 tw-rounded-full tw-w-full tw-h-[40px] md:tw-h-[60px] tw-flex tw-justify-between tw-items-center tw-shadow-sm"
                >
                    <q-input
                        class="tw-ml-2 md:tw-ml-6 tw-self-center tw-w-full newsletter-input-mobile tw-text-xs md:tw-text-base"
                        :label="$t('fields.email')"
                        v-model="form.email"
                        :error="form.errors.email"
                        type="email"
                        @update:model-value="form.errors.email = undefined"
                        required
                        borderless
                        autocomplete="off"
                    />
                    <button
                        type="button"
                        @click="submit"
                        :disabled="!canSubmit"
                        :class="{
                            'tw-opacity-50 tw-cursor-not-allowed': !canSubmit,
                        }"
                        class="tw-bg-primary tw-text-white tw-rounded-full tw-font-bold tw-text-sm md:tw-text-base tw-leading-4 tw-uppercase tw-px-4 md:tw-px-10 md:tw-py-4 tw-flex-shrink-0 tw-whitespace-nowrap tw-h-[30px] md:tw-h-auto"
                    >
                        {{ $t("pre_footer.contact.button") }}
                    </button>
                </q-form>
                <div class="tw-flex tw-items-center tw-mt-3 tw-ml-1">
                    <q-checkbox
                        v-model="form.privacy_policy_accepted"
                        color="primary"
                        size="sm"
                        dense
                    />
                    <span
                        class="tw-text-xs md:tw-text-sm tw-ml-1 tw-text-primary"
                    >
                        {{ $t("pre_footer.contact.accept_privacy") }}
                        <NuxtLink
                            to="/information/seg-priv-policy"
                            class="tw-underline tw-font-semibold"
                            >{{
                                $t("settings.seg_and_privacy_policy")
                            }}</NuxtLink
                        >
                    </span>
                </div>
                <TurnstileWidget
                    v-if="turnstileEnabled && turnstileSiteKey"
                    ref="turnstileMobileRef"
                    v-model="turnstileToken"
                    :site-key="turnstileSiteKey"
                    size="flexible"
                    theme="light"
                    class="tw-mt-3 tw-flex tw-justify-center"
                />
                <span
                    class="tw-text-negative tw-ml-4 tw-text-sm tw-mt-1"
                    v-if="form.errors.email"
                    >{{ form.errors.email[0] }}</span
                >
            </div>

            <!-- Social Brands -->
            <Brands color="primary" />
        </div>
    </div>
    <div v-if="shopListStore.currentShop" class="tw-bg-white tw-w-full">
        <div
            class="max-container tw-w-full tw-flex tw-flex-col tw-py-16 tw-gap-6"
        >
            <p
                class="tw-font-semibold tw-text-[32px] tw-leading-[48px] tw-text-customGray-700"
            >
                {{ $t("stores.info_title") }}
                {{ shopListStore.currentShop.name }}
            </p>
            <p
                class="tw-text-customGray-500"
                v-if="shopListStore?.currentShop?.informations.enable_about_us"
            >
                {{ shopListStore?.currentShop?.informations?.about_us }}
            </p>
            <p
                v-if="shopListStore?.currentShop?.url"
                class="tw-text-customGray-500"
            >
                {{ $t("stores.more_info_title") }}
                <a :href="shopListStore?.currentShop?.url">{{
                    shopListStore?.currentShop?.url
                }}</a>
            </p>
            <div
                v-if="
                    shopListStore?.currentShop?.informations
                        .enable_return_policy
                "
            >
                <q-separator class="tw-text-customGray-200" />
                <FooterStoreInformationItem
                    :content="
                        shopListStore?.currentShop?.informations.return_policy
                    "
                    :title="$t('stores.settings.customization.return_policy')"
                ></FooterStoreInformationItem>
            </div>
            <div
                v-if="
                    shopListStore?.currentShop?.informations
                        .enable_warranty_policy
                "
            >
                <q-separator class="tw-text-customGray-200" />
                <FooterStoreInformationItem
                    :content="
                        shopListStore?.currentShop?.informations.warranty_policy
                    "
                    :title="$t('stores.settings.customization.warranty_policy')"
                ></FooterStoreInformationItem>
            </div>
            <div
                v-if="
                    shopListStore?.currentShop?.informations
                        .enable_shipment_policy
                "
            >
                <q-separator class="tw-text-customGray-200" />
                <FooterStoreInformationItem
                    :content="
                        shopListStore?.currentShop?.informations.shipment_policy
                    "
                    :title="$t('stores.settings.customization.shipment_policy')"
                ></FooterStoreInformationItem>
            </div>
            <div v-if="shopListStore?.currentShop?.contacts">
                <q-separator class="tw-text-customGray-200" />
                <div class="tw-flex tw-justify-around tw-w-full tw-py-8">
                    <div
                        class="tw-flex tw-flex-col tw-bg-white tw-gap-4 tw-text-customGray-700"
                    >
                        <p class="tw-text-customGray-500">
                            {{ $t("social.contacts") }}:
                        </p>
                        <div>
                            <q-icon
                                name="fa-solid fa-location-dot"
                                color="gray"
                            />
                            <span class="tw-mx-1"
                                >{{ $t("fields.address") }}:
                            </span>
                            <span
                                @click="
                                    openNetwork(
                                        'http://www.google.com/maps/place/' +
                                            shopListStore?.currentShop?.contacts
                                                ?.latitude +
                                            ',' +
                                            shopListStore?.currentShop.contacts
                                                ?.longitude
                                    )
                                "
                            >
                                {{
                                    shopListStore?.currentShop?.contacts
                                        ?.address
                                }}
                            </span>
                        </div>
                        <div>
                            <q-icon name="fa-solid fa-phone" color="gray" />
                            <span class="tw-mx-1"
                                >{{ $t("fields.phones") }}:
                            </span>
                            {{
                                Array.isArray(
                                    shopListStore?.currentShop?.contacts?.phones
                                )
                                    ? shopListStore?.currentShop?.contacts?.phones.join(
                                          ", "
                                      )
                                    : (shopListStore?.currentShop?.contacts
                                          ?.phones ?? "")
                            }}
                        </div>
                        <div>
                            <q-icon name="fa-solid fa-envelope" color="gray" />
                            <span class="tw-mx-1"
                                >{{ $t("fields.email") }}:
                            </span>
                            {{ shopListStore?.currentShop?.contacts?.email }}
                        </div>
                        <Brands
                            v-if="
                                storeBrands(shopListStore?.currentShop).length >
                                0
                            "
                            :networks="storeBrands(shopListStore?.currentShop)"
                            color="black"
                            size="24px"
                        ></Brands>
                    </div>
                    <div
                        v-if="shopListStore?.currentShop?.contacts?.schedule"
                        class="tw-flex tw-flex-col tw-gap-4 tw-text-customGray-700"
                    >
                        <p class="tw-text-customGray-500">
                            {{ $t("fields.schedule") }}:
                        </p>
                        <span
                            class="tw-flex tw-gap-2"
                            v-for="schedule in shopListStore?.currentShop
                                ?.contacts?.schedule"
                        >
                            <span class="tw-font-bold tw-flex tw-gap-1"
                                >{{ scheduleDays(schedule.day_week) }}: </span
                            ><span>{{ scheduleHours(schedule.hours) }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.prefooter-container::before {
    content: "";
    width: 500px;
    position: absolute;
    left: 0;
    bottom: 0;
    aspect-ratio: 1 / 1;
    background: url("/img/banner-img.svg") no-repeat;
}
</style>
